import { useCallback, useEffect, useRef, useState } from 'react';
import { getAlerts, ackAlert, type Alert } from '../api.js';
import { unackedCount, badgeLabel, ruleTitle } from '../lib/alertsBadge.js';
import Icon from '../ui/Icon.js';

const POLL_MS = 60_000;

/**
 * Header bell: shows a badge with the number of unacknowledged alerts, polls
 * every 60s, and opens a dropdown to read and acknowledge them.
 */
export default function AlertsBell(): JSX.Element {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const refresh = useCallback(async () => {
    try {
      setAlerts(await getAlerts(true));
    } catch {
      /* a lapsed session is handled by the api wrapper's redirect */
    }
  }, []);

  // Initial load + polling.
  useEffect(() => {
    void refresh();
    const id = window.setInterval(() => void refresh(), POLL_MS);
    return () => window.clearInterval(id);
  }, [refresh]);

  // Close the dropdown on an outside click.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent): void {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  async function acknowledge(id: string): Promise<void> {
    try {
      await ackAlert(id);
      setAlerts((prev) => prev.filter((a) => a.id !== id));
    } catch {
      /* ignore; next poll reconciles */
    }
  }

  const count = unackedCount(alerts);
  const label = badgeLabel(count);

  return (
    <div className="alertsbell bc-bell" ref={rootRef}>
      <button
        className="alertsbell__btn"
        aria-label={count > 0 ? `${count} unread alerts` : 'Alerts'}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <Icon name="bell" size={20} />
        {label && <span className="alertsbell__badge">{label}</span>}
      </button>

      {open && (
        <div className="alertsbell__panel" role="menu">
          <div className="alertsbell__head">Alerts</div>
          {alerts.length === 0 ? (
            <p className="alertsbell__empty">
              <Icon name="check" size={18} /> All clear
            </p>
          ) : (
            <ul className="alertsbell__list">
              {alerts.map((a) => (
                <li key={a.id} className="alertsbell__item">
                  <div className="alertsbell__item-main">
                    <div className="alertsbell__item-title">{ruleTitle(a)}</div>
                    {a.message && <div className="alertsbell__item-msg">{a.message}</div>}
                  </div>
                  <button
                    className="alertsbell__ack"
                    onClick={() => void acknowledge(a.id)}
                    aria-label="Dismiss alert"
                  >
                    <Icon name="check" size={16} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
